<template>
<v-card class="elevation-12">
	<v-toolbar
		color="primary"
		dark
		flat>
		<v-toolbar-title>Registration Form</v-toolbar-title>
	</v-toolbar>
	<v-card-text>
		<v-form :disabled="loading"
			ref="form"
			v-model="formValid"
			lazy-validation>

			<h4 class="mt-2 mb-n1 text-center">
				Main Details
			</h4>
			<v-row>
				<v-col cols="12" md="6">
					<v-text-field
						autofocus
						label="Firstname"
						prepend-icon="mdi-account"
						v-model="item.firstname"
						@input="uppercase(item, 'firstname')"
						placeholder="Juan"
						:rules="[rules.required]"
						autocomplete="off">

						<template v-slot:append-outer>
							<v-select style="margin-top: -16px"
								v-model="item.extname"
								:items="nameExtensions"
								item-text="text"
								item-value="value"
								label="Jr. Sr. III">
							</v-select>
						</template>
					</v-text-field>
				</v-col>

				<v-col cols="12" md="3">
					<v-text-field 
						v-model="item.middlename" 
						@input="uppercase(item, 'middlename')"
						prepend-icon="mdi-account"
						label="Middlename"
						autocomplete="off"/>
				</v-col>
				<v-col cols="12" md="3">
					<v-text-field 
						v-model="item.lastname" 	
						@input="uppercase(item, 'lastname')"					
						prepend-icon="mdi-account"
						label="Lastname"
						:rules="[rules.required]"
						autocomplete="off"/>
				</v-col>
			</v-row>

			<v-row>
				<v-col cols="12" md="4">
					<v-select
						v-model="item.sex"
						:items="sexes"
						item-text="text"
						item-value="value"
						label="Sex"
						prepend-icon="mdi-gender-male-female">
					</v-select>
				</v-col>

				<v-col cols="12" md="4">
					<v-dialog
						ref="BirthdateDialog"
						v-model="birthdateDialog"
						:return-value.sync="item.birthdate"
						persistent
						width="290px">
						<template v-slot:activator="{ on }">
							<v-text-field
								v-model="item.birthdate"
								label="Birthdate"
								prepend-icon="mdi-calendar"
								v-on="on"
								:rules="[rules.required, rules.legalAge]"
								placeholder="YYYY-MM-DD"
								hint="Must be atleast 16 years old"
								readonly
								autocomplete="off"
							></v-text-field>
						</template>
						<v-date-picker ref="BirthdatePicker" v-model="item.birthdate" scrollable>
							<v-spacer></v-spacer>
							<v-btn text color="primary" @click="birthdateDialog = false">Cancel</v-btn>
							<v-btn text color="primary" @click="$refs.BirthdateDialog.save(item.birthdate)">OK</v-btn>
						</v-date-picker>
					</v-dialog>
				</v-col>

				<v-col cols="12" md="4">
					<v-select
						v-model="item.civilStatus"
						:items="civilStatuses"
						:rules="[rules.required]"
						label="Civil Status"
						prepend-icon="mdi-gender-male-female">
					</v-select>
				</v-col>
			</v-row>
										
			<v-row>
				<v-col cols="12" md="4">
					<v-select
						v-model="item.region"
						:items="regions"
						:rules="[rules.required]"
						item-text="regDesc"
						item-value="regDesc"
						label="Region"
						@change="refreshAddress(getCode(item.region, regions, 'region'), 'provinces', 'province')"
						prepend-icon="mdi-map-marker">
					</v-select>
						<!-- @change="refreshAddress('region', 'regions', Api.address.getProvinces, context, 'provinces')" -->

				</v-col>
				<v-col cols="12" md="4">
					<v-select
						v-model="item.province"
						:items="provinces"
						:rules="[rules.required]"
						item-text="provDesc"
						item-value="provDesc"
						label="Province"
						@change="refreshAddress(getCode(item.province, provinces, 'province'), 'cities', 'city')"
						prepend-icon="mdi-map-marker">
					</v-select>
				</v-col>

				<v-col cols="12" md="4">
					<v-select
						v-model="item.city"
						:items="cities"
						item-text="citymunDesc"
						item-value="citymunDesc"
						:rules="[rules.required]"
						label="City / Municipality"
						@change="refreshAddress(getCode(item.city, cities, 'city'), 'barangays', 'barangay')"
						prepend-icon="mdi-map-marker">
					</v-select>
				</v-col>
			</v-row>

			<v-row>
				<v-col cols="12" md="4">
					<v-select
						v-model="item.barangay"
						:items="barangays"
						item-text="brgyDesc"
						item-value="brgyDesc"
						:rules="[rules.required]"
						label="Barangay"
						prepend-icon="mdi-map-marker">
					</v-select>
				</v-col>

				<v-col cols="12" md="4">
					<v-select
						v-model="item.district"
						:items="districts"
						item-text="text"
						item-value="value"
						label="District"
						prepend-icon="mdi-map-marker">
					</v-select>
				</v-col>

				<v-col cols="12" md="4">
					<v-text-field 
						v-model="item.street" 
						prepend-icon="mdi-map-marker"
						label="Street"
						:rules="[rules.required]"
						@input="uppercase(item, 'street')"
						autocomplete="off"
						required/>
				</v-col>
			</v-row>

			<h4 class="mt-2 text-center">
				Additional Details
			</h4>

			<v-row>
				<v-col cols="12" md="4">
					<v-select
						v-model="item.birthRegion"
						:items="regions"
						item-text="regDesc"
						item-value="regDesc"
						:rules="[rules.required]"
						label="Birth Region"
						@change="refreshAddress(getCode(item.birthRegion, regions, 'region'), 'birthProvinces',  'province')"
						prepend-icon="mdi-map-marker">
					</v-select>

				</v-col>
				<v-col cols="12" md="4">
					<v-select
						v-model="item.birthProvince"
						:items="birthProvinces"
						item-text="provDesc"
						item-value="provDesc"
						:rules="[rules.required]"
						label="Birth Province"
						@change="refreshAddress(getCode(item.birthProvince, birthProvinces, 'province'), 'birthCities',  'city')"
						prepend-icon="mdi-map-marker">
					</v-select>
				</v-col>

				<v-col cols="12" md="4">
					<v-select
						v-model="item.birthCity"
						:items="birthCities"
						item-text="citymunDesc"
						item-value="citymunDesc"
						:rules="[rules.required]"
						label="Birth City / Municipality"
						prepend-icon="mdi-map-marker">
					</v-select>
				</v-col>
			</v-row>

			<v-row>
				<v-col cols="12" md="4">
					<v-text-field 
						v-model="item.email" 
						prepend-icon="mdi-email"
						label="Email"
						placeholder="ex. juandelacruz@gmail.com"
						:rules="[rules.required, rules.email]"
						hint="Enrollment details will be sent here"
						autocomplete="off"/>
				</v-col>

				<v-col cols="12" md="3">
					<v-text-field 
						v-model="item.mobile" 
						prepend-icon="mdi-cellphone-android"
						label="Mobile Number"
						placeholder="ex: 09123456789"
						hint="You will be notified using this number"
						:rules="[rules.required, rules.mobile]"
						autocomplete="off">
						<!-- :rules="[rules.required, rules.mobile, rules.existing]" -->
						<!-- <template v-slot:append-outer>
							<v-btn small 
								@click="verify"
								:disabled="verified">
								<v-icon left v-show="verified">mdi-check</v-icon>
								{{ (verified)? 'Verified' : 'Verify'}}
							</v-btn>
						</template> -->
					</v-text-field>
				</v-col>

				<v-col cols="12" md="5">
					<v-text-field 
						v-model="item.social" 
						prepend-icon="mdi-facebook"
						label="Social Media Account"
						placeholder="ex. @juandelacuz"
						hint="Will be used to reach you"
						autocomplete="off"/>
				</v-col>
			</v-row>

			<v-row>
				<v-col cols="12" md="3">
					<v-text-field 
						v-model="item.bloodtype"
						@input="uppercase(item, 'bloodtype')"
						:rules=[rules.required]
						prepend-icon="fa-tint"
						label="Blood Type"
						placeholder="ex. A, B, O"
						autocomplete="off"/>
				</v-col>
				<v-col cols="12" md="5">
					<v-select
						v-model="item.education"
						:items="educations"
						label="Educational Attainment"
						prepend-icon="mdi-map-marker"
						:rules=[rules.required]>
					</v-select>
				</v-col>

				<v-col cols="12" md="4">
					<v-select
						v-model="item.employmentStatus"
						:items="['Employed', 'Unemployed']"
						label="Employment Status"
						:rules="[rules.required]"
						prepend-icon="mdi-account-hard-hat"
						hint="Before the training"
						persistent-hint>
					</v-select>
				</v-col>
			</v-row>
			<!-- <v-row justify="center">
				<v-col cols="12" md="4">
					<v-text-field 
						v-model="item.employmentSchedule" 
						prepend-icon="mdi-account-hard-hat"
						label="Vacant Schedule"
						hint="Ex. Weekdays 6:00PM to 9:00PM"
						:disabled="item.employmentStatus != 'Employed'"
						autocomplete="off"
						required/>
				</v-col>
			</v-row> -->

			<v-row justify="center">
				<v-col cols="12" md="4">
					<v-select
						@change="
							switch(selectedDisability) {
								case 0: item.disability = ''; break;
								case 1: item.disability = 'Pregnant'; break;
								case 2: item.disability = ''; break;							
							}
						"
						v-model="selectedDisability"
						:items="disabilities"
						label="Disability"
						prepend-icon="mdi-wheelchair-accessibility">
					</v-select>
				</v-col>
				
				<v-col cols="12" md="4">
					<v-text-field 
						v-model="item.disability" 
						prepend-icon="mdi-wheelchair-accessibility"
						label="Specify"
						autocomplete="off"
						:disabled="selectedDisability != 2"
						required/>
				</v-col>
			</v-row>

			<!-- <v-row justify="center">
				<v-col cols="12" md="4">
					<v-text-field
						v-model="item.password"
						prepend-icon="mdi-lock"
						:append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
						:rules="[rules.required, rules.min]"
						:type="showPass ? 'text' : 'password'"
						label="Password"
						hint="At least 4 characters"
						autocomplete="off"
						@click:append="showPass = !showPass"
					></v-text-field>
				</v-col>

				<v-col cols="12" md="4">
					<v-text-field
						v-model="item.repeatPassword"
						prepend-icon="mdi-lock"
						:append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
						:rules="[rules.required, rules.min, rules.repeatPassword]"
						:type="showPass ? 'text' : 'password'"
						label="Repeat password"
						hint="At least 6 characters"
						autocomplete="off"
						@click:append="showPass = !showPass"
					></v-text-field>
				</v-col>
			</v-row> -->

			<!-- <v-row justify="center">
				<v-col cols="12" md="8">
					<v-text-field 
						v-model="item.ncertificate" 
						prepend-icon="mdi-barcode"
						label="National Certificate Number"
						autocomplete="off"
						hint="FORMAT: (BPP NC II - 20123456789101 - January 16, 2020)"
						required/>
				</v-col>
			</v-row> -->

			<h4 class="mt-2 text-center">
				Emergency Contact Details
			</h4>

			<v-row>
				<v-col cols="12" md="4">
					<v-text-field 
						v-model="item.emergencyPerson" 	
						prepend-icon="mdi-account"
						label="Emergency Person"
						@input="uppercase(item, 'emergencyPerson')"
						:rules="[rules.required]"
						autocomplete="off"/>
				</v-col>

				<v-col cols="12" md="3">
					<v-text-field 
						v-model="item.emergencyNumber" 
						prepend-icon="mdi-cellphone-android"
						label="Contact Number"
						placeholder="ex: 09123456789"
						:rules="[rules.required, rules.mobile]"
						autocomplete="off">
					</v-text-field>
				</v-col>

				<v-col cols="12" md="5">
					<v-text-field 
						v-model="item.emergencyAddress" 
						prepend-icon="mdi-map-marker"
						label="Address"
						:rules="[rules.required]"
						@input="uppercase(item, 'emergencyAddress')"
						autocomplete="off"
						required/>
				</v-col>
			</v-row>

			<div v-if="!updateMode">
				<h4 class="mt-2 text-center">
					Course Details
				</h4>
				<p class="text-center mb-n3">Click / tap the checkbox below.</p>
				<!-- <CourseSelector @course-selected="courseSelectedHandler"/> -->
				<RegisterCourseSelector @course-selected="courseSelectedHandler">
				</RegisterCourseSelector>


				<h4 class="mt-3 text-center">
					Identification Details
				</h4>

				<v-row justify="center">
					<v-img src="/img/umid_sample.png" max-width="300"></v-img>
				</v-row>
				<p class="text-center">
					Upload a <b>clear</b> picture of any of your <b>Identification Card</b> and <b>Birth Certificate</b> to verify your identity.
					<br>
					(Preferably government issued ID or latest high school ID if none)
				</p>
				<FileUploader label="ID Picture"
					@file-uploaded="(handleObj) => { handleFileUpload(handleObj, 'identificationUploadId') }"
					@reset-upload="
						item.identificationUploadId = null;
					">
				</FileUploader>
				
				<FileUploader label="Birth Certificate / NSO"
					@file-uploaded="(handleObj) => { handleFileUpload(handleObj, 'birthcertUploadId') }"
					@reset-upload="
						item.birthcertUploadId = null;
					">
				</FileUploader>
			</div>
			
		</v-form>

	</v-card-text>
	<v-card-actions>
		<v-spacer />
		<v-btn v-if="!updateMode"
			:disabled="loading"
			@click="resetFields()">
			Reset
		</v-btn>
		<v-btn v-if="!updateMode"
			color="primary"
			:disabled="loading"
			@click="register">
			Register
		</v-btn>

		<v-btn v-if="updateMode"
			:disabled="loading"
			@click="$emit('dialog-closed')">
			Close
		</v-btn>
		<v-btn v-if="updateMode"
			color="primary"
			:disabled="loading"
			@click="update">
			Update
		</v-btn>
	</v-card-actions>
</v-card>


</template>

<script>
import FormDataMixin from '../../mixins/form-data.mixin'
import FormRulesMixin from '../../mixins/form-rules.mixin'
import RegisterCreateMixin from './mixins/register-create.mixin'
import RegisterUpdateMixin from './mixins/register-update.mixin'

import RegisterCourseSelector from './RegisterCourseSelector'
// import CourseSelector from './CourseSelector'
import FileUploader from '../file/FileUploader'

export default {
	name: 'RegisterForm',
	mixins: [ FormDataMixin, FormRulesMixin, RegisterCreateMixin, RegisterUpdateMixin ],
	// components: { CourseSelector, FileUploader },
	components: { RegisterCourseSelector, FileUploader },
}
</script>